import { type SelectHTMLAttributes, forwardRef, useId } from 'react';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { value, onChange, options, placeholder, error, disabled, className, id: providedId, ...props },
    ref,
  ) => {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const errorId = `${id}-error`;

    return (
      <div className="w-full">
        <select
          ref={ref}
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'w-full rounded-lg border bg-white px-4 py-2.5 text-sm outline-none transition-colors',
            'focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]',
            'disabled:cursor-not-allowed disabled:bg-[#f5f5f5] disabled:text-[#999]',
            error
              ? 'border-[#c62828] focus:border-[#c62828] focus:ring-[#c62828]'
              : 'border-[#e0e0e0]',
            className,
          )}
          {...props}
        >
          {placeholder !== undefined && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={errorId} className="mt-1.5 text-xs text-[#c62828]" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
